<template>
  <div :class="layoutLevelOneCssClass">
    <AddUserModal
      v-if="showAddUserModal"
      :company="company"
      :role="currentAddUserRole"
      :sso-entities="ssoEntities"
      :typeahead-users="typeaheadUsers"
      @close-add-modal="closeAddModal"
      @refresh-users="refreshUsers"
    ></AddUserModal
    ><AddGroupModal
      v-if="showAddGroupModal"
      :typeahead-groups="typeaheadGroups"
      @close-add-modal="closeAddModal"
      @refresh-users="refreshUsers"
    ></AddGroupModal>
    <div v-if="loaded" :class="layoutLevelTwoCssClass">
      <div :class="layoutLevelThreeCssClass">
        <div v-if="canShowFirmUsers" class="sf-below-4">
          <div class="sf-level sf-level-between sf-pad-h-3 sf-below-1">
            <div class="sf-font-m sf-fwdb sf-c-text-dark">
              {{ I18n.t('user.staff') }}
            </div>
            <a
              v-if="canEditFirmUsers"
              class="sf-btn sf-btn-small"
              @click.prevent="openAddUserModal('firm')"
              >{{ I18n.t('user.add_staff_contributor') }}</a
            >
          </div>
          <div class="sf-tab sf-below-2">
            <div v-if="canEditFirmUsers" class="sf-tab-item">
              <div class="sf-level" data-testid="visible-for-staff-checkbox">
                <SfCheckbox
                  v-model="company.visibleForStaff"
                  class="visible-for-staff"
                  :label="I18n.t('company.visible_for_all_staff')"
                  @click="toggleVisiblityForContributorsWithConfirm"
                ></SfCheckbox>
              </div>
            </div>
            <div
              v-for="role in firmRoles"
              :key="`${role.userId}-${role.role}`"
              class="sf-tab-item sf-hover"
            >
              <EditUserModal
                v-if="role.userId"
                v-show="false"
                :ref="'editModal' + role.userId"
                :can-edit-external-contributors="canEditExternalContributors"
                :can-edit-firm-users="canEditFirmUsers"
                :company-name="company.name"
                :firm-supports-admin-users="company.firmSupportsAdminUsers"
                :user="role"
                @refresh-users="refreshUsers"
              ></EditUserModal>
              <div
                :class="{ 'sf-dim-el': role.userId === currentUserId }"
                class="sf-level sf-level-between"
              >
                <div>
                  <a
                    v-if="isCurrentUserFirmAdmin"
                    :href="userUrl(role.userId)"
                    class="sf-a sf-c-primary"
                    ><span>{{ role.userName }}</span
                    ><span v-if="role.userId === currentUserId">{{
                      ' | ' + I18n.t('user.you')
                    }}</span></a
                  ><span v-else
                    ><span>{{ role.userName }}</span
                    ><span v-if="role.userId === currentUserId">{{
                      ' | ' + I18n.t('user.you')
                    }}</span></span
                  ><span
                    v-if="role.usersAdmin && company.firmSupportsAdminUsers"
                    class="sf-c-text-light sf-pad-l-1"
                    >{{ I18n.t('user.user_administrator') }}</span
                  >
                </div>
                <a
                  v-if="canEditFirmUsers && role.userId !== currentUserId"
                  class="sf-btn sf-btn-small sf-hover-item"
                  @click.prevent="openEditUserModal(role.userId)"
                  >{{ I18n.t('common.edit') }}</a
                >
              </div>
            </div>
          </div>
          <div v-if="firmRoles.length == 0" class="sf-layer-empty">
            {{ I18n.t('user.empty_states.staff') }}
          </div>
        </div>
        <div class="sf-below-4">
          <div class="sf-level sf-level-between sf-pad-h-3 sf-below-1">
            <div class="sf-font-m sf-fwdb sf-c-text-dark">
              {{ I18n.t('user.pulse_users') }}
            </div>
            <a
              v-if="allowedActions.editUsers"
              class="sf-btn sf-btn-small"
              @click.prevent="openAddUserModal('customer')"
              >{{ I18n.t('user.add_pulse_access_btn') }}</a
            >
          </div>
          <div v-if="customerRoles.length > 0" class="sf-tab">
            <div
              v-for="role in customerRoles"
              :key="`${role.userId}-${role.role}`"
              class="sf-tab-item"
            >
              <EditUserModal
                v-if="role.userId"
                v-show="false"
                :ref="'editModal' + role.userId"
                :can-edit-external-contributors="canEditExternalContributors"
                :can-edit-firm-users="canEditFirmUsers"
                :company-name="company.name"
                :firm-supports-admin-users="company.firmSupportsAdminUsers"
                :user="role"
                @refresh-users="refreshUsers"
              ></EditUserModal>
              <div
                :class="{ 'sf-dim-el': role.userId === currentUserId }"
                class="sf-level sf-level-between sf-hover"
              >
                <div>
                  <a
                    v-if="isCurrentUserFirmAdmin"
                    :href="userUrl(role.userId)"
                    class="sf-a sf-c-primary"
                    ><span>{{ role.userName }}</span
                    ><span v-if="role.userId === currentUserId">{{
                      ' | ' + I18n.t('user.you')
                    }}</span></a
                  ><span v-else
                    ><span>{{ role.userName }}</span
                    ><span v-if="role.userId === currentUserId">{{
                      ' | ' + I18n.t('user.you')
                    }}</span></span
                  ><span
                    v-if="role.usersAdmin && company.firmSupportsAdminUsers"
                    class="sf-c-text-light sf-pad-l-1"
                    >{{ I18n.t('user.user_administrator') }}</span
                  >
                </div>
                <a
                  v-if="
                    allowedActions.editUsers && role.userId !== currentUserId
                  "
                  class="sf-btn sf-btn-small sf-hover-item"
                  @click.prevent="openEditUserModal(role.userId)"
                  >{{ I18n.t('common.edit') }}</a
                >
              </div>
            </div>
          </div>
          <div v-else class="sf-layer-empty">
            {{ I18n.t('user.empty_states.pulse') }}
          </div>
        </div>
        <div v-if="canShowExternalContributors" class="sf-below-4">
          <div class="sf-level sf-level-between sf-pad-h-3 sf-below-1">
            <div class="sf-font-m sf-fwdb sf-c-text-dark">
              {{ I18n.t('user.external_contributors') }}
            </div>
            <a
              v-if="
                canEditExternalContributors &&
                (externalContributorRoles.length > 0 || !showExternalIsPaid())
              "
              class="sf-btn sf-btn-small"
              @click.prevent="openAddUserModal('external')"
              >{{ I18n.t('user.add_external_contributor') }}</a
            >
          </div>
          <SfBannerPage
            v-if="canEditExternalContributors && showExternalIsPaid()"
            :title="
              externalContributorRoles.length == 0
                ? I18n.t('user.external_activation_title')
                : undefined
            "
            :variant="bannerVariants.info"
            class="sf-below-2"
            :has-long-text="true"
            is-full-width
          >
            {{ I18n.t('user.external_activation_text') }}
            <template #actions>
              <div
                v-if="externalContributorRoles.length == 0"
                class="sf-btn"
                type="button"
                @click.prevent="openAddUserModal('external')"
              >
                {{ I18n.t('user.external_activation_button') }}
              </div>
            </template>
          </SfBannerPage>
          <div v-if="externalContributorRoles.length > 0" class="sf-tab">
            <div
              v-for="role in externalContributorRoles"
              :key="`${role.userId}-${role.role}`"
              class="sf-tab-item sf-hover"
            >
              <EditUserModal
                v-if="role.userId"
                v-show="false"
                :ref="'editModal' + role.userId"
                :can-edit-external-contributors="canEditExternalContributors"
                :can-edit-firm-users="canEditFirmUsers"
                :company-name="company.name"
                :firm-supports-admin-users="company.firmSupportsAdminUsers"
                :user="role"
                @refresh-users="refreshUsers"
              ></EditUserModal>
              <div
                :class="{ 'sf-dim-el': role.userId === currentUserId }"
                class="sf-level sf-level-between"
              >
                <div>
                  <a
                    v-if="isCurrentUserFirmAdmin"
                    :href="userUrl(role.userId)"
                    class="sf-a sf-c-primary"
                    ><span>{{ role.userName }}</span
                    ><span v-if="role.userId === currentUserId">{{
                      ' | ' + I18n.t('user.you')
                    }}</span></a
                  ><span v-else
                    ><span>{{ role.userName }}</span
                    ><span v-if="role.userId === currentUserId">{{
                      ' | ' + I18n.t('user.you')
                    }}</span></span
                  ><span
                    v-if="role.usersAdmin && company.firmSupportsAdminUsers"
                    class="sf-c-text-light sf-pad-l-1"
                    >{{ I18n.t('user.user_administrator') }}</span
                  >
                </div>
                <a
                  v-if="
                    canEditExternalContributors && role.userId !== currentUserId
                  "
                  class="sf-btn sf-btn-small sf-hover-item"
                  @click.prevent="openEditUserModal(role.userId)"
                  >{{ I18n.t('common.edit') }}</a
                >
              </div>
            </div>
          </div>
          <div
            v-else-if="!(canEditExternalContributors && showExternalIsPaid())"
            class="sf-layer-empty"
          >
            {{ I18n.t('user.empty_states.external_contributor') }}
          </div>
        </div>
        <div v-if="allowedActions.showGroups" class="sf-below-4">
          <div class="sf-level sf-level-between sf-pad-h-3 sf-below-1">
            <div class="sf-font-m sf-fwdb sf-c-text-dark">
              {{ I18n.t('groups.title') }}
            </div>
            <a
              v-if="allowedActions.addGroup"
              class="sf-btn sf-btn-small"
              @click.prevent="openAddGroupModal"
              >{{ I18n.t('user.add_group') }}</a
            >
          </div>
          <div v-if="groups.length > 0" class="sf-tab">
            <div
              v-for="group in groups"
              :key="group.id"
              class="sf-tab-item sf-hover"
            >
              <div class="sf-level sf-level-between">
                <span>{{ group.name }}</span
                ><a
                  v-if="allowedActions.addGroup"
                  class="sf-btn sf-btn-danger sf-btn-small sf-hover-item"
                  @click.prevent="deleteGroup(group.id)"
                  >{{ I18n.t('common.remove') }}</a
                >
              </div>
            </div>
          </div>
          <div v-else class="sf-layer-empty">
            {{ I18n.t('user.empty_states.groups') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../api/index'
import AddUserModal from './add-user-modal'
import EditUserModal from './edit-user-modal'
import AddGroupModal from './add-group-modal'
import { SfCheckbox } from '@silverfin/ui-components'
import SfBannerPage from '@app/shared/vue/components/sf-banner/sf-banner-page.vue'
import { bannerVariants } from '@app/shared/types/banner'
import { showExternalIsPaid, userUrl } from '@app/shared/company-users/utils'

const CompanyUsers = {
  components: {
    AddUserModal,
    EditUserModal,
    AddGroupModal,
    SfCheckbox,
    SfBannerPage,
  },

  props: {
    isRelaxedLayout: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      ssoEntities: [],
      groups: [],
      company: {},
      staffAccessCount: 0,
      firmRoles: [],
      customerRoles: [],
      externalContributorRoles: [],
      typeaheadUsers: [],
      typeaheadGroups: [],
      showAddUserModal: false,
      showAddGroupModal: false,
      allowedActions: {},
      currentAddUserRole: '',
      currentUserId: parseInt(Silverfin.currentUserId()),
      loaded: false,
      bannerVariants,
    }
  },

  computed: {
    relaxedLayout() {
      // default mode would be false, but for wizard this needed to be relaxed
      // so unless explicitly set on the main HTML component
      // layout won't be relaxed
      return this.isRelaxedLayout === 'true'
    },

    layoutLevelOneCssClass() {
      if (this.relaxedLayout) {
        return ''
      } else {
        return 'sf-grid sf-below-4 sf-border-box'
      }
    },

    layoutLevelTwoCssClass() {
      if (this.relaxedLayout) {
        return ''
      } else {
        return 'sf-row sf-row-center'
      }
    },

    layoutLevelThreeCssClass() {
      if (this.relaxedLayout) {
        return ''
      } else {
        return 'sf-col sf-xs-12 sf-sm-6'
      }
    },

    allUserRoles() {
      return this.firmRoles
        .concat(this.customerRoles)
        .concat(this.externalContributorRoles)
    },

    rolesWillBeRemoved() {
      return this.firmRoles
        .filter(
          (role) =>
            role.userStaffAccess &&
            !role.usersAdmin &&
            role.userId !== parseInt(Silverfin.currentUserId())
        )
        .map((role) => role.userName)
        .join(', ')
    },

    currentUserCompanyRole() {
      const currentUserRole = this.allUserRoles.find(
        (role) => role.userId === parseInt(Silverfin.currentUserId())
      )
      return currentUserRole ? currentUserRole.role : ''
    },

    isCurrentUserFirmAdmin() {
      return Silverfin.isCurrentUserAdmin()
    },

    canShowFirmUsers() {
      return this.currentUserCompanyRole === 'firm'
    },

    canEditFirmUsers() {
      return this.allowedActions.editUsers && this.canShowFirmUsers
    },

    canShowExternalContributors() {
      return this.canShowFirmUsers || this.currentUserCompanyRole === 'external'
    },

    canEditExternalContributors() {
      return this.allowedActions.editUsers && this.canShowExternalContributors
    },
  },

  mounted() {
    this.getCompanyUsers()
  },

  methods: {
    getCompanyUsers() {
      api.getUsers().then((data) => {
        this.setData(data)
      })
    },

    setData(data) {
      this.resetExistingRoles()
      this.loaded = false
      this.staffAccessCount = data['staffAccessCount']
      this.firmRoles = data['firmRoles']
      this.customerRoles = data['customerRoles']
      this.externalContributorRoles = data['externalContributorRoles']
      this.allowedActions = data['allowedActions']
      this.company = data['company']
      this.groups = data['groups']
      this.ssoEntities = data['ssoEntities']
      this.loaded = true
    },

    getTypeaheadUsers() {
      api.getTypeaheadUsers().then((response) => {
        // Filter to eliminate already assigned users on typeahead list
        const userIds = this.allUserRoles.map((u) => u['userId'])
        this.typeaheadUsers = response['users'].filter(
          (user) =>
            !userIds.includes(user.id) &&
            !(this.company.visibleForStaff && user.staffAccess)
        )
      })
    },

    getTypeaheadGroups() {
      api.getTypeaheadGroups().then((response) => {
        // Filter to eliminate already assigned groups on typeahead list
        const groupIds = this.groups.map((u) => u['id'])
        this.typeaheadGroups = response.filter(
          (group) => !groupIds.includes(group.id)
        )
      })
    },

    openAddUserModal(role) {
      this.getTypeaheadUsers()
      this.currentAddUserRole = role
      this.showAddUserModal = true
    },

    openAddGroupModal() {
      this.getTypeaheadGroups()
      this.showAddGroupModal = true
    },

    openEditUserModal(index) {
      this.$refs['editModal' + index][0].$el.style.display = 'block'
    },

    closeAddModal() {
      this.showAddUserModal = false
      this.showAddGroupModal = false
    },

    resetExistingRoles() {
      this.firmRoles = []
      this.customerRoles = []
      this.externalContributorRoles = []
    },

    refreshUsers() {
      this.loaded = false
      this.currentAddUserRole = ''
      this.closeAddModal()
      this.getCompanyUsers()
      this.getTypeaheadUsers()
    },

    toggleCompanyVisible() {
      this.company.visibleForStaff = !this.company.visibleForStaff
    },

    toggleVisiblityForContributors() {
      api
        .companyToggleVisibility()
        .catch(() => this.toggleCompanyVisible())
        .then(
          () =>
            this.company.visibleForStaff &&
            this.rolesWillBeRemoved !== '' &&
            this.refreshUsers()
        )
    },

    toggleVisiblityForContributorsWithConfirm() {
      // warning! this method is called _before_ the toggling of this.company.visibleForStaff happens
      // due to the checkbox clicking. That's why !this.company.visibleForStaff is checked
      // as we want to show the confirm screen only if this.company.visibleForStaff is false
      // and is toggled to true.
      const message =
        this.rolesWillBeRemoved !== '' &&
        I18n.t('company.some_roles_will_be_removed_warning', {
          users: this.rolesWillBeRemoved,
        })

      if (!this.company.visibleForStaff) {
        const escapedCompanyName =
          window.Silverfin.GlobalConfirmationModal.escape(this.company.name)
        window.sfConfirm({
          question: I18n.t('company.make_visible_to_all_title_html', {
            company_name: escapedCompanyName,
            count: this.staffAccessCount,
          }),
          message: message,
          confirmLabel: I18n.t('company.role_removed_confirm_btn'),
          cancelLabel: I18n.t('js.global_confirmation_modal.default.cancel'),
          onConfirm: this.toggleVisiblityForContributors,
          onCancel: this.toggleCompanyVisible,
          options: {
            message_class: 'sf-bg-snow sf-c-text',
            confirm_class: 'sf-btn-warning',
          },
        })
      } else {
        this.toggleVisiblityForContributors()
      }
    },

    deleteGroup(id) {
      api.deleteGroup(id)

      const group = this.groups.find((group) => group.id === id)
      const index = this.groups.indexOf(group)
      delete this.groups[index]

      this.getCompanyUsers()
    },

    userUrl(userId) {
      return userUrl(userId)
    },

    showExternalIsPaid() {
      return showExternalIsPaid(this.company, this.externalContributorRoles)
    },
  },
}

export default CompanyUsers
</script>
